// Order-level compatibility rules between chassisType and the order's
// size / equipType / chassisProvider / isTank.
//
// The size<->chassis part is driven by a shared compatibility MATRIX whose
// canonical source is the backend (app/core/chassis_compatibility.py, served at
// GET /chassis/size-compatibility) and is also consumed by the saverule
// order_equipsize — so the requirement is defined once and the UI and saverule
// can't drift. (This replaces the v1 split where sr_equipsize and
// lib_editrecord.pl each hardcoded the matrix and disagreed on STANDARD+48.)
// The equipType (ISO TANK) and chassisProvider couplings are validation-only
// (no saverule equivalent), so they stay here.
//
// Each rule lists the `fields` it involves so callers can flag every field
// participating in a violated rule (chassis type can't satisfy size means
// both chassisType and size are highlighted — user can fix either side).
//
// Callers should pass the *effective* values — edited where present, otherwise
// the loaded order value — plus the matrix (from useChassisCompatibility).
// Returns an array of structured errors; an empty array means the tuple is valid.

export type ChassisField = 'chassisType' | 'size' | 'equipType' | 'chassisProvider' | 'isTank';

export interface ChassisValidationError {
  fields: ReadonlyArray<ChassisField>;
  message: string;
}

export interface ChassisSizeRule {
  /** Container sizes this chassis type may carry (e.g. ['20','40']). */
  sizes: string[];
  /** Whether a blank size is permitted for this chassis type. */
  allowBlank: boolean;
}

/** chassistype -> rule. A chassistype absent from the map has no size constraint. */
export type ChassisCompatibilityMatrix = Record<string, ChassisSizeRule>;

// Bundled mirror of the backend canonical table. Used as the synchronous
// bootstrap/fallback for save-blocking validation; the runtime fetch
// (useChassisCompatibility) overrides it with the backend's copy. The two are
// kept in lockstep by chassisValidation.test.ts + the backend tests.
// RECONCILED: STANDARD allows 48 (matches v1 lib_editrecord.pl / the live UI);
// v1 sr_equipsize cleared it, which is treated as a bug — see the backend module.
export const DEFAULT_CHASSIS_MATRIX: ChassisCompatibilityMatrix = {
  STANDARD: { sizes: ['20', '40', '45', '48', '53'], allowBlank: false },
  '93K TRIAXLE': { sizes: ['20', '40'], allowBlank: false },
  COMBO: { sizes: ['20', '40'], allowBlank: false },
  SLIDER: { sizes: ['20'], allowBlank: false },
  TRIAXLE: { sizes: ['20'], allowBlank: false },
  'SUPER LIGHT': { sizes: ['20'], allowBlank: false },
  'TANK CHASSIS': { sizes: ['20'], allowBlank: false },
  'ULTRA LIGHT': { sizes: ['40', '45'], allowBlank: false },
  'DOUBLE DROP': { sizes: ['20'], allowBlank: true },
  FLATBED: { sizes: ['20', '40', '45', '48'], allowBlank: true },
  '48 SINGLE DROP': { sizes: ['20', '40'], allowBlank: true },
  '53 SINGLE DROP': { sizes: ['20', '40', '45'], allowBlank: true },
};

function sizeAllowed(rule: ChassisSizeRule, size: string): boolean {
  if (size === '') return rule.allowBlank;
  return rule.sizes.includes(size);
}

// Human description of a rule's allowed sizes for the error message, e.g.
// '20', '20 or 40', '20/40/45/48/53', '20/40/45/48 or blank'.
function describeAllowed(rule: ChassisSizeRule): string {
  const parts = [...rule.sizes];
  if (rule.allowBlank) return `${parts.join('/')} or blank`;
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} or ${parts[1]}`;
  return parts.join('/');
}

export function validateChassisCompatibility(
  chassisType: string | null | undefined,
  size: string | null | undefined,
  equipType: string | null | undefined,
  chassisProvider: string | null | undefined,
  isTank: string | null | undefined,
  matrix: ChassisCompatibilityMatrix = DEFAULT_CHASSIS_MATRIX,
  opts?: { isTemplate?: boolean },
): ChassisValidationError[] {
  const errors: ChassisValidationError[] = [];
  // Template nuances (V1 template save gate): a template allows a BLANK size for
  // any chassis type (orders require a valid non-blank size for STANDARD/TRIAXLE/
  // COMBO/etc.), and a template does NOT require a chassis provider (orders do).
  const isTemplate = !!opts?.isTemplate;
  const ct = (chassisType ?? '').trim();
  const sz = (size ?? '').trim();
  const et = (equipType ?? '').trim();
  const provider = (chassisProvider ?? '').trim();
  const tank = (isTank ?? '').trim();

  // The ISO TANK + isTank='Y' coupling is independent of chassisType — runs
  // even when no chassis type is set, so an ISO TANK order without the tank
  // flag is flagged immediately.
  if (et === 'ISO TANK' && tank !== 'Y') {
    errors.push({
      fields: ['equipType', 'isTank'],
      message: 'Pro must be set to Tank=Y in order to use ISO TANK equipment',
    });
  }

  if (ct === '') return errors;

  // Size <-> chassis: driven entirely by the shared canonical matrix. Templates
  // treat a blank size as always allowed (only a non-blank invalid size errors).
  const rule = matrix[ct];
  const blankOkForTemplate = isTemplate && sz === '';
  if (rule && !blankOkForTemplate && !sizeAllowed(rule, sz)) {
    errors.push({
      fields: ['chassisType', 'size'],
      message: `Size must be ${describeAllowed(rule)} to use a ${ct}`,
    });
  }

  if ((ct === 'TANK CHASSIS' && et !== 'ISO TANK') || (ct !== 'TANK CHASSIS' && et === 'ISO TANK')) {
    errors.push({ fields: ['chassisType', 'equipType'], message: 'ISO TANK must use TANK CHASSIS' });
  }
  if (!isTemplate && !provider) {
    errors.push({ fields: ['chassisType', 'chassisProvider'], message: 'Must have a chassis provider to have a chassis type' });
  }

  return errors;
}

// True when any error in `errors` lists `field` as one of its involved fields.
// Used by UI surfaces to drive the red highlight on every participant of a
// violated rule.
export function chassisErrorsContainField(
  errors: ReadonlyArray<ChassisValidationError>,
  field: ChassisField,
): boolean {
  return errors.some((e) => e.fields.includes(field));
}
